import { ImportAttempt, importAttemptsApi } from '../import-attempts/import-attempts.api';
import { createApi } from '@reduxjs/toolkit/query/react';
import { InvoiceDto, InvoicePaginationDto } from '../../models/invoice.dto';
import { baseQueryWithRedirect } from '../base-query-with-redirect';
import {AmountChangedDto} from '../../models/amount-changed.dto';
import { BankAccountDto } from '../../models/bank-account.dto';
import { AccountingNumberDto } from '../../models/accounting-number.dto';

type UpdateInvoiceBody = {
   invoiceId: number;
   data: InvoiceDto;
};

type InvoiceId = {
   invoiceId: number;
};

type FetchAmountChanged = {
   invoiceId: number;
};

type RejectInvoice = {
   invoiceId: number;
   reason: string;
};

type ExportInvoice = {
   invoiceIds: number[];
   paymentDate: string;
   fileType: string;
};

export type FilterInvoiceBody = {
   fromDate: string;
   toDate: string;
   fromAmount: string;
   toAmount: string;
   name: string;
   costCenter: string;
   accountingNumber: string;
   generationDateFilter: 'ALL' | 'GENERATED' | 'PENDING';
   sortBy?: string;
   ascending?: boolean;
   page: number;
   size: number;
};

type CreditorDebtorDto = {
   name: string;
   street: string;
   buildingNumber: string;
   postalCode: string;
   city: string;
   country: string;
}

export type CreateNonQRInvoice = {
   bankAccount: BankAccountDto | undefined;
   amount: number | undefined;
   currency: string;
   companyAccount: string;
   companyBic: string;
   company: CreditorDebtorDto;
   reference: string;
   paymentDate: string;
   vatCode: string;
   accountingNumber: AccountingNumberDto;
   description: string;
}

const downloadFileHandler = async (response) => {
   const link = document.createElement('a');
   link.href = window.URL.createObjectURL(await response.blob());
   link.setAttribute('download', getFileName(response));
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
};

const getFileName = (response) => {
   const contentDisposition = response.headers.get('content-disposition');
   return contentDisposition?.split('filename=')[1].split(';')[0];
};

const buildFilterQuery = (baseUrl, params) => {
   const baseParams = new URLSearchParams({
      amountFrom: params.fromAmount,
      amountTo: params.toAmount,
      paymentDateFrom: params.fromDate,
      paymentDateTo: params.toDate,
      company: params.name,
      generationDateFilter: params.generationDateFilter,
      sortBy: params.sortBy,
      ascending: params.ascending,
      page: params.page,
      size: params.size
   });

   if (params.costCenter) {
      baseParams.append('costCenter', params.costCenter);
   }

   if (params.accountingNumber) {
      baseParams.append('accountingNumber', params.accountingNumber);
   }

   return baseUrl + '?' + baseParams.toString();
};

export const invoicesApi = createApi({
   reducerPath: 'invoices/api',
   tagTypes: ['Invoice', 'UpdateInvoice'],
   baseQuery: baseQueryWithRedirect,
   endpoints: (build) => ({
      filterInvoices: build.query<InvoicePaginationDto, FilterInvoiceBody>({
         query: (params) => ({
            url: buildFilterQuery('invoice', params),
            method: 'GET',
         }),
         providesTags: (result) =>
            result
               ? [
                  { type: 'Invoice', id: 'LIST' },
                  ...result.content.map((i) => ({ type: 'Invoice' as const, id: i.invoiceId })),
               ]
               : [{ type: 'Invoice', id: 'LIST' }],
      }),

      deleteInvoice: build.mutation<UpdateInvoiceBody, InvoiceId>({
         query: ({ invoiceId }: InvoiceId) => ({
            url: `invoice/${invoiceId}`,
            method: 'POST'
         }),
         invalidatesTags: (result, error, arg) => [{ type: 'Invoice', invoiceId: arg.invoiceId }],
      }),

      uploadInvoices: build.mutation<ImportAttempt[], FormData>({
         query: (formData) => ({
            url: 'invoice/uploads',
            method: 'POST',
            body: formData,
         }),
         async onQueryStarted(_, { dispatch, queryFulfilled }) {
            try { await queryFulfilled; } catch { /* Upload component displays translated errors. */ }
            dispatch(importAttemptsApi.util.invalidateTags(['Summary', 'Activity']));
         },
         invalidatesTags: [{ type: 'Invoice' }],
      }),

       createNonQrInvoiceManually: build.mutation<{ success: boolean; message: string }, { data: CreateNonQRInvoice, file?: File }>({
         query: ({ data, file }) => {
            const formData = new FormData();
            formData.append('invoice', new Blob([JSON.stringify(data)], { type: 'application/json' }));

            if (file) {
               formData.append('receipt', file);
            }

            return {
               url: 'invoice/non-qr',
               method: 'POST',
               body: formData,
               formData: true,
            };
         },
         invalidatesTags: [{ type: 'Invoice' }],
      }),
      updateInvoice: build.mutation<UpdateInvoiceBody, UpdateInvoiceBody>({
         query: ({ invoiceId, data }: UpdateInvoiceBody) => ({
            url: `invoice/${invoiceId}`,
            method: 'PUT',
            body: data,
         }),
         invalidatesTags: (_res, _err, arg) => [{ type: 'Invoice', id: arg.invoiceId }],
      }),

      exportInvoices: build.mutation<ExportInvoice, ExportInvoice>({
         query: ({ invoiceIds, paymentDate }: ExportInvoice) => ({
            url: 'payment',
            method: 'POST',
            body: { invoiceIds, paymentDate },
            responseHandler: downloadFileHandler,
            cache: 'no-cache',
         }),
         invalidatesTags: ['Invoice'],
      }),

      exportCsv: build.mutation<void, FilterInvoiceBody>({
         query: (params) => ({
            url: buildFilterQuery('invoice/csv', params),
            method: 'GET',
            responseHandler: downloadFileHandler,
            cache: 'no-cache',
         }),
      }),

      downloadInvoice: build.mutation({
         query: ({ invoiceId }: InvoiceId) => ({
            url: `invoice/download/${invoiceId}`,
            method: 'GET',
            responseHandler: downloadFileHandler,
            cache: 'no-cache',
         }),
      }),

      downloadInvoiceReceipt: build.mutation({
         query: ({ invoiceId }: InvoiceId) => ({
            url: `invoice/download/${invoiceId}/receipt`,
            method: 'GET',
            responseHandler: downloadFileHandler,
            cache: 'no-cache',
         }),
      }),

      downloadInvoices: build.mutation<number[], number[]>({
         query: (invoiceIds: number[]) => ({
            url: 'invoice/download',
            method: 'POST',
            body: invoiceIds,
            responseHandler: downloadFileHandler,
            cache: 'no-cache',
         }),
      }),

      approveInvoice: build.mutation({
         query: ({ invoiceId }: InvoiceId) => ({
            url: `invoice/approval/${invoiceId}`,
            method: 'POST',
            cache: 'no-cache',
         }),
         invalidatesTags: ['Invoice'],
      }),

      approveInvoices: build.mutation<InvoiceDto[], number[]>({
         query: (invoiceIds) => ({
            url: 'invoice/approval',
            method: 'POST',
            body: invoiceIds,
         }),
         invalidatesTags: ['Invoice'],
      }),
      rejectInvoice: build.mutation({
         query: ({ invoiceId, reason }: RejectInvoice) => ({
            url: `invoice/reject/${invoiceId}`,
            method: 'POST',
            cache: 'no-cache',
            body: reason
         }),
         invalidatesTags: ['Invoice'],
      }),

      getAmountChanged: build.query<AmountChangedDto[], FetchAmountChanged>({
         query: ({invoiceId}: FetchAmountChanged) => ({
            url: `invoice/amount_changed/${invoiceId}`,
            method: 'GET',
         }),
         providesTags: ['Invoice']
      }),
   }),
});

export const {
   useFilterInvoicesQuery,
   useDeleteInvoiceMutation,
   useUploadInvoicesMutation,
   useCreateNonQrInvoiceManuallyMutation,
   useUpdateInvoiceMutation,
   useExportInvoicesMutation,
   useExportCsvMutation,
   useDownloadInvoiceMutation,
   useDownloadInvoiceReceiptMutation,
   useDownloadInvoicesMutation,
   useApproveInvoiceMutation,
   useApproveInvoicesMutation,
   useRejectInvoiceMutation,
   useGetAmountChangedQuery,
} = invoicesApi;
